import { useEffect, useState } from "react";
import axios from "axios";
import { useLang } from "../i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUSES = [
  { v: "present", icon: "✅", color: "bg-emerald-500/15 border-emerald-500/40 text-emerald-400" },
  { v: "late", icon: "🕒", color: "bg-amber-500/15 border-amber-500/40 text-amber-400" },
  { v: "excused", icon: "📋", color: "bg-sky-500/15 border-sky-500/40 text-sky-400" },
  { v: "absent", icon: "❌", color: "bg-[#e05555]/15 border-[#e05555]/40 text-[#e05555]" },
];

export default function AttendanceModal({ trainingId, onClose, onSaved }) {
  const { t } = useLang();
  const password = localStorage.getItem("st_admin_pw") || "";
  const headers = { "X-Admin-Password": password };

  const [training, setTraining] = useState(null);
  const [players, setPlayers] = useState([]);
  const [attendance, setAttendance] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/admin/trainings/${trainingId}`, { headers }),
      axios.get(`${API}/admin/players`, { headers }),
    ]).then(([trRes, plRes]) => {
      setTraining(trRes.data);
      setPlayers(plRes.data);
      setAttendance(trRes.data.attendance || {});
    });
    // eslint-disable-next-line
  }, [trainingId]);

  const setStatus = (pid, status) => {
    setAttendance((a) => {
      const next = { ...a };
      if (next[pid] === status) delete next[pid];
      else next[pid] = status;
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.post(
        `${API}/admin/trainings/${trainingId}/attendance`,
        { attendance },
        { headers }
      );
      onSaved();
    } finally {
      setSaving(false);
    }
  };

  const totals = STATUSES.reduce((acc, s) => {
    acc[s.v] = Object.values(attendance).filter((x) => x === s.v).length;
    return acc;
  }, {});

  return (
    <div
      className="fixed inset-0 z-[110] bg-black/75 backdrop-blur-sm flex items-start sm:items-center justify-center p-4 overflow-y-auto"
      data-testid="attendance-modal"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-3xl bg-[#161616] border border-[#252525] rounded-2xl p-5 sm:p-7 my-8"
      >
        <div className="flex items-start justify-between mb-4 pb-4 border-b border-[#252525] gap-3">
          <div className="min-w-0">
            <h2 className="font-bebas text-2xl sm:text-3xl tracking-widest text-white">
              ✓ {t("mark_attendance")}
            </h2>
            {training && (
              <p className="text-sm text-neutral-400 mt-1 truncate">
                {training.title} · {training.date} {training.time}
              </p>
            )}
          </div>
          <button onClick={onClose} className="text-neutral-400 hover:text-white text-2xl leading-none flex-shrink-0">×</button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-5">
          {STATUSES.map((s) => (
            <div key={s.v} className={`rounded-lg px-3 py-2 border text-center ${s.color}`}>
              <div className="font-bebas text-2xl tracking-widest">{totals[s.v]}</div>
              <div className="text-[10px] uppercase tracking-widest opacity-80 mt-0.5">{s.icon} {t(s.v)}</div>
            </div>
          ))}
        </div>

        <div className="space-y-1.5 max-h-[55vh] overflow-y-auto pr-1">
          {players.map((p) => {
            const cur = attendance[p.id];
            return (
              <div
                key={p.id}
                data-testid={`att-row-${p.player_number}`}
                className="rounded-lg bg-[#0f0f0f] border border-[#252525] px-3 py-2.5 flex items-center gap-3"
              >
                <span className="font-bebas text-lg text-[#e8559a] tracking-widest w-12 flex-shrink-0">
                  #{p.player_number}
                </span>
                <span className="flex-1 min-w-0 truncate text-sm text-white">
                  {p.first_name} {p.last_name}
                </span>
                <div className="flex gap-1.5 flex-shrink-0">
                  {STATUSES.map((s) => (
                    <button
                      key={s.v}
                      type="button"
                      onClick={() => setStatus(p.id, s.v)}
                      data-testid={`att-${p.player_number}-${s.v}`}
                      title={t(s.v)}
                      className={`w-9 h-9 rounded-md border text-sm transition-all ${
                        cur === s.v ? s.color + " ring-2 ring-current/40" : "bg-[#161616] border-[#252525] hover:border-[#e8559a]/40"
                      }`}
                    >
                      {s.icon}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-5 py-2 rounded-lg border border-[#252525] hover:border-[#e8559a]/50 text-neutral-300 text-sm transition-colors"
          >
            {t("cancel")}
          </button>
          <button
            onClick={save}
            disabled={saving}
            data-testid="save-attendance-btn"
            className="px-6 py-2 rounded-lg bg-[#e8559a] hover:bg-[#c9407f] disabled:opacity-60 text-white font-bebas text-lg tracking-widest transition-colors"
          >
            {saving ? "..." : t("save_attendance")}
          </button>
        </div>
      </div>
    </div>
  );
}
